import React, { createContext, useCallback, useContext, useMemo, useState, useEffect } from 'react';

const PresentationContext = createContext(null);
const STORAGE_KEY = 'tw:presentation:on';

// A pool of pseudonyms shown as "First L." so anything that hashes to slot i
// stays stable for the whole session (browser tab).
const FIRST_POOL = [
    'Maria', 'James', 'Ana', 'David', 'Elena', 'Michael', 'Lucia', 'Robert',
    'Sofia', 'Thomas', 'Grace', 'Daniel', 'Emma', 'Peter', 'Rosa', 'Frank',
    'Nina', 'Henry', 'Clara', 'Oscar', 'Iris', 'Sam', 'Ruth', 'Leon',
];
const LAST_POOL = 'STHMKLBRDNJPFCGWA'.split('');

// Cheap stable hash → slot index
function stringHash(s) {
    let h = 5381;
    for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
    return Math.abs(h);
}

export function PresentationProvider({ children }) {
    const [on, setOn] = useState(() => sessionStorage.getItem(STORAGE_KEY) === '1');

    useEffect(() => { sessionStorage.setItem(STORAGE_KEY, on ? '1' : '0'); }, [on]);

    const setPresentation = useCallback((next) => setOn(!!next), []);
    const toggle = useCallback(() => setOn(v => !v), []);

    const pseudonymFor = useCallback((realName) => {
        if (!realName) return realName;
        const idx = stringHash(realName);
        const first = FIRST_POOL[idx % FIRST_POOL.length];
        const last = LAST_POOL[(idx >> 4) % LAST_POOL.length];
        return `${first} ${last}.`;
    }, []);

    const householdFor = useCallback((realName) => {
        if (!realName) return realName;
        const idx = stringHash(realName);
        const letter = String.fromCharCode(65 + (idx % 26));
        return `Household ${letter}`;
    }, []);

    const doctorFor = useCallback((doctor) => {
        if (!doctor) return doctor;
        // Preserve "Dr. K, GI" style — take specialty from string if present after comma.
        const s = String(doctor).trim();
        const specialty = s.includes(',') ? s.split(',').slice(1).join(',').trim() : '';
        // Use hash so same doctor → same letter
        const idx = stringHash(s);
        const letter = String.fromCharCode(65 + (idx % 26));
        return specialty ? `Dr. ${letter}, ${specialty}` : `Dr. ${letter}`;
    }, []);

    const ageFromDob = useCallback((dob) => {
        if (!dob) return dob;
        try {
            const d = new Date(dob);
            if (Number.isNaN(d.getTime())) return dob;
            const now = new Date();
            let age = now.getFullYear() - d.getFullYear();
            const m = now.getMonth() - d.getMonth();
            if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
            return `age ${age}`;
        } catch { return dob; }
    }, []);

    const value = useMemo(() => ({
        on,
        setPresentation,
        toggle,
        // Helpers — pass through when off, mask when on
        maskName: (n) => (on && n ? pseudonymFor(n) : n),
        maskHousehold: (n) => (on && n ? householdFor(n) : n),
        maskDoctor: (n) => (on && n ? doctorFor(n) : n),
        maskDob: (dob) => (on && dob ? ageFromDob(dob) : dob),
        maskPhone: (v) => (on && v ? '•••-•••-••••' : v),
        maskEmail: (v) => (on && v ? '•••@•••' : v),
        maskAddress: (v) => (on && v ? '•••' : v),
        // Free-text is where private details live — hide entirely
        maskText: (v) => (on && v ? 'Hidden in presentation mode' : v),
        // Coordinator-authored titles/summaries are OK but truncate to 40 chars in presentation
        maskSummary: (v, len = 40) => {
            if (!v) return v;
            if (!on) return v;
            const s = String(v);
            return s.length <= len ? s : s.slice(0, len).trimEnd() + '…';
        },
    }), [on, setPresentation, toggle, pseudonymFor, householdFor, doctorFor, ageFromDob]);

    return <PresentationContext.Provider value={value}>{children}</PresentationContext.Provider>;
}

export function usePresentation() {
    const ctx = useContext(PresentationContext);
    if (!ctx) throw new Error('usePresentation must be used within PresentationProvider');
    return ctx;
}
